import dayjs from "dayjs"
import { take } from "lodash-es"
import { defineStore } from "pinia"
import { getQueryObject } from "../libs/utils"

interface SSRStatisticsData {
  referer: string, ip: string, userAgent: string, route: string, query: Record<string, string>, test?: any
}

export interface ArchiveData {
  ssrStatisticsData: SSRStatisticsData
  networkConnectFailed: boolean
}

export const useDYArchiveStore = defineStore('nuxt3-module-doyac-archive', () => {
  //로컬 스토리지 값을 스토어 방식으로 동작시킴. 이 경우 알아서 parsing됨
  const ssrStatisticsData = ref<SSRStatisticsData>({
    referer: '',
    ip: '',
    userAgent: '',
    route: '',
    query: {}
  })

  /**
   * 2026-09-08 [신설] 이 단말에서 **차단망 특유의 접속 실패**가 확인되었는가.
   *
   * 중국 등에서는 구글·페이스북 계열 도메인이 통째로 막혀 스크립트가 **응답 없이 타임아웃**된다.
   * 그 사실을 아는 주체가 둘이라 여기 모아 둔다.
   *   ① 네이티브 앱 — 구글 접속 시도의 타임아웃으로 먼저 안다.
   *      `window.dySetNetworkConnectFailed(true)` 로 알려준다(plugins/network-connect.client.ts).
   *   ② 웹 자신 — 페이스북 SDK 로드가 **타임아웃**되면 그것이 곧 증거다(DYShare.vue).
   *
   * 🔴 **«응답 없음» 만 근거로 삼는다.** `onerror` 로 즉시 실패하는 것(광고차단기·확장·CSP)이나
   *   SDK 호출 자체가 던진 예외는 그 단말만의 사정이라 네트워크가 막힌 근거가 되지 못한다.
   *   아무 실패나 여기 담으면 멀쩡한 사용자의 기능까지 꺼진다.
   *
   * 🔴 **user 스토어가 아니라 여기** 인 이유 = user 스토어에는 persist 설정이 없어서
   *   페이지를 넘길 때마다 초기화된다. 그러면 막힌 사용자가 페이지마다 다시 실패를 겪는다.
   *   이 스토어는 이미 sessionStorage 로 영속되므로 탭이 살아 있는 동안 유지된다.
   *   ⚠️ localStorage 가 아닌 이유 = 중국을 벗어나도 영영 남으면 안 되기 때문.
   *      (판정은 `isChina && networkConnectFailed` 라 스스로 풀리긴 하지만 굳이 오래 남길 값이 아니다)
   */
  const networkConnectFailed = ref(false)

  /**
   * 🔴 이 값만 **직접 sessionStorage 에 넣고 뺀다.** 스토어의 `persist` 에 맡기지 않는다.
   *   이 스토어의 `persist` 는 `persistedState.sessionStorage` 를 쓰는데, 실측(2026-09-08)상
   *   **아무것도 기록되지 않는다**(sessionStorage 가 통째로 비어 있음. 같은 앱의 다른 스토어인
   *   `nuxt3-module-doyac-recent-checked` 는 기본 localStorage 라 정상 기록된다).
   *   그 설정을 고치면 `ssrStatisticsData` 까지 3사이트에서 갑자기 영속되기 시작하므로
   *   이 작업 범위에서 건드리지 않고, 필요한 값 하나만 명시적으로 처리한다.
   */
  const NETWORK_CONNECT_FAILED_KEY = 'dy-network-connect-failed'

  /** 값을 바꾸면서 sessionStorage 에도 남긴다. 쓰기 실패(프라이빗 모드 등)는 무시한다. */
  function setNetworkConnectFailed(failed: boolean) {
    networkConnectFailed.value = !!failed
    if (!import.meta.client) return
    try {
      if (failed) sessionStorage.setItem(NETWORK_CONNECT_FAILED_KEY, '1')
      else sessionStorage.removeItem(NETWORK_CONNECT_FAILED_KEY)
    } catch (e) {
      // 저장 못 해도 이번 페이지 안에서는 동작한다. 다음 페이지에서 한 번 더 겪을 뿐이다.
    }
  }

  /** 페이지 진입 시 이전에 확인된 값을 복구한다(plugins/network-connect.client.ts 에서 호출). */
  function restoreNetworkConnectFailed() {
    if (!import.meta.client) return
    try {
      if (sessionStorage.getItem(NETWORK_CONNECT_FAILED_KEY) === '1') networkConnectFailed.value = true
    } catch (e) {
      //
    }
  }

  return {
    ssrStatisticsData,
    networkConnectFailed,
    setNetworkConnectFailed,
    restoreNetworkConnectFailed
  }
}, {
  persist: { //세션스토리지 값을 스토어로 쓰기 위함
    storage: persistedState.sessionStorage,
  }
  
})